<template>
  <MainLayout>
    <button class="bot-control" :disabled="syncing" @click="syncListings">
      <span v-if="syncing" class="btn-loading"></span>
      <span v-else>Обновить из Авито</span>
    </button>
    <p v-if="syncing" class="sync-hint">Забираем объявления и историю заказов, это занимает до минуты…</p>

    <!-- Вкладки -->
    <div class="tabs">
      <button
        v-for="tab in tabs"
        :key="tab.state"
        class="tab"
        :class="{ active: state === tab.state }"
        @click="switchTab(tab.state)"
      >
        {{ tab.title }} <span class="tab-count">{{ counts[tab.state] }}</span>
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Загрузка объявлений...</p>
    </div>

    <div v-else-if="listings.length === 0" class="empty-state">
      <p>{{ emptyText }}</p>
    </div>

    <div v-else class="listings">
      <div v-for="listing in listings" :key="listing.avito_id" class="listing-card">
        <div class="listing-head">
          <a class="listing-title" :href="listing.url" @click.prevent="openListing(listing)">
            {{ listing.title }}
          </a>
          <div class="listing-meta">
            <span v-if="listing.price">{{ formatPrice(listing.price) }}</span>
            <span class="listing-id">№ {{ listing.avito_id }}</span>
          </div>
        </div>

        <!-- Привязанные товары -->
        <div v-if="listing.products.length" class="chips">
          <span v-for="product in listing.products" :key="product.id" class="chip chip-mapped">
            {{ product.name }}
            <button
              class="chip-remove"
              title="Отвязать"
              :disabled="busyId === listing.avito_id"
              @click="saveProducts(listing, listing.products.filter(p => p.id !== product.id).map(p => p.id))"
            >×</button>
          </span>
        </div>

        <!-- Подсказки -->
        <div v-if="state === 'unmapped'" class="suggestions">
          <button
            v-for="suggestion in listing.suggestions"
            :key="suggestion.product_id"
            class="suggestion"
            :disabled="busyId === listing.avito_id"
            @click="saveProducts(listing, [suggestion.product_id])"
          >
            <img
              v-if="suggestion.photo_url"
              :src="suggestion.photo_url"
              alt="Фото товара"
              class="product-thumb"
              loading="lazy"
              @error="hideBrokenImage"
            >
            <span v-else class="product-thumb product-thumb-empty"></span>
            <span class="suggestion-name">{{ suggestion.product_name }}</span>
            <span class="suggestion-reason" :class="'reason-' + suggestion.reason">
              {{ reasonText[suggestion.reason] }}
            </span>
          </button>
          <p v-if="!listing.suggestions.length" class="no-suggestions">Похожих товаров не нашлось</p>
        </div>

        <!-- Выбор товара вручную -->
        <div v-if="pickerFor === listing.avito_id" class="picker">
          <input
            v-model="pickerQuery"
            type="text"
            class="search-input"
            placeholder="Поиск товара..."
          />
          <div class="picker-list">
            <label v-for="product in pickerResults" :key="product.id" class="picker-item">
              <input v-model="pickerSelected" type="checkbox" :value="product.id" />
              <img
                v-if="product.photo_url"
                :src="product.photo_url"
                alt="Фото товара"
                class="product-thumb"
                loading="lazy"
                @error="hideBrokenImage"
              >
              <span v-else class="product-thumb product-thumb-empty"></span>
              <span>{{ product.name }}</span>
            </label>
            <p v-if="!pickerResults.length" class="no-suggestions">Ничего не найдено</p>
          </div>
          <p class="picker-hint">Можно выбрать несколько товаров, если в объявлении продаются разные цвета</p>
          <div class="picker-actions">
            <button
              class="btn btn-primary"
              :disabled="!pickerSelected.length || busyId === listing.avito_id"
              @click="saveProducts(listing, pickerSelected)"
            >
              Сохранить ({{ pickerSelected.length }})
            </button>
            <button class="btn btn-secondary" @click="closePicker">Отмена</button>
          </div>
        </div>

        <!-- Действия -->
        <div v-else class="listing-actions">
          <button
            v-if="state !== 'ignored'"
            class="btn btn-secondary"
            :disabled="busyId === listing.avito_id"
            @click="openPicker(listing)"
          >
            {{ state === 'mapped' ? 'Изменить' : 'Другой товар…' }}
          </button>
          <button
            v-if="state === 'unmapped'"
            class="btn btn-secondary"
            :disabled="busyId === listing.avito_id"
            @click="setIgnored(listing, true)"
          >
            Не товар магазина
          </button>
          <button
            v-if="state === 'ignored'"
            class="btn btn-secondary"
            :disabled="busyId === listing.avito_id"
            @click="setIgnored(listing, false)"
          >
            Вернуть в разметку
          </button>
        </div>
      </div>
    </div>

    <!-- Уведомления -->
    <div v-if="notification.show" class="notification" :class="notification.type">
      <span class="notification-text">{{ notification.message }}</span>
      <button class="notification-close" @click="notification.show = false">×</button>
    </div>
  </MainLayout>
</template>

<script>
import api from '@/api/api';
import MainLayout from '@/components/MainLayout.vue';

// Синхронизация с Авито тянет объявления и заказы за 90 дней — дольше стандартного таймаута
const SYNC_TIMEOUT_MS = 120000;

export default {
  name: 'AvitoListingsPage',

  components: {
    MainLayout,
  },

  data() {
    return {
      state: 'unmapped',
      tabs: [
        { state: 'unmapped', title: 'Без товара' },
        { state: 'mapped', title: 'Размечены' },
        { state: 'ignored', title: 'Скрытые' },
      ],
      reasonText: {
        orders: 'по заказам',
        similar_listing: 'похожее объявление',
        name: 'по названию',
      },
      listings: [],
      counts: { unmapped: 0, mapped: 0, ignored: 0 },
      products: [],
      loading: false,
      syncing: false,
      busyId: null,
      pickerFor: null,
      pickerQuery: '',
      pickerSelected: [],
      notification: { show: false, message: '', type: 'success' },
      notificationTimer: null,
    };
  },

  computed: {
    managerTgId() {
      const tgId = localStorage.getItem('tg_id');
      return tgId ? Number(tgId) : null;
    },

    emptyText() {
      if (this.state === 'unmapped') return 'Все активные объявления размечены';
      if (this.state === 'mapped') return 'Пока нет размеченных объявлений';
      return 'Нет объявлений с отметкой «Не товар магазина»';
    },

    pickerResults() {
      const query = this.pickerQuery.trim().toLowerCase();
      const selected = new Set(this.pickerSelected);
      const matches = this.products.filter(p => !query || p.name.toLowerCase().includes(query));
      // Выбранные товары всегда наверху, чтобы их можно было снять
      return [
        ...this.products.filter(p => selected.has(p.id)),
        ...matches.filter(p => !selected.has(p.id)).slice(0, 30),
      ];
    },
  },

  async mounted() {
    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.onClick(this.backButtonOnClick);
      window.Telegram.WebApp.BackButton.show();
    }

    await Promise.all([this.fetchListings(), this.fetchProducts()]);

    // Первое открытие: объявлений в базе ещё нет — сразу забираем их из Авито
    const total = this.counts.unmapped + this.counts.mapped + this.counts.ignored;
    if (total === 0) {
      await this.syncListings();
    }
  },

  beforeUnmount() {
    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.offClick(this.backButtonOnClick);
      window.Telegram.WebApp.BackButton.hide();
    }
    clearTimeout(this.notificationTimer);
  },

  methods: {
    backButtonOnClick() {
      if (this.pickerFor) {
        this.closePicker();
        return;
      }
      window.history.back();
    },

    /* -------------------- DATA -------------------- */

    async fetchListings() {
      this.loading = true;
      try {
        const { data } = await api.get('/avito_listings', {
          params: { manager_tg_id: this.managerTgId, state: this.state },
        });
        this.listings = data.listings || [];
        this.counts = data.counts || this.counts;
      } catch (error) {
        this.handleApiError(error, 'загрузке объявлений');
        this.listings = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts() {
      try {
        const { data } = await api.get('/avito_listings/products', {
          params: { manager_tg_id: this.managerTgId },
        });
        this.products = data || [];
      } catch (error) {
        this.handleApiError(error, 'загрузке товаров');
      }
    },

    async syncListings() {
      this.syncing = true;
      try {
        const { data } = await api.post(
          '/avito_listings/sync',
          { manager_tg_id: this.managerTgId },
          { timeout: SYNC_TIMEOUT_MS },
        );
        let message = `Активных объявлений: ${data.active}, новых: ${data.created}`;
        if (data.deactivated) message += `, снято с Авито: ${data.deactivated}`;
        if (!data.order_history_loaded) message += '. Историю заказов получить не удалось';
        this.showNotification(message, data.order_history_loaded ? 'success' : 'warning');
        await this.fetchListings();
      } catch (error) {
        this.handleApiError(error, 'обновлении из Авито');
      } finally {
        this.syncing = false;
      }
    },

    switchTab(state) {
      if (this.state === state) return;
      this.state = state;
      this.closePicker();
      this.fetchListings();
    },

    /* -------------------- РАЗМЕТКА -------------------- */

    async saveProducts(listing, productIds) {
      this.busyId = listing.avito_id;
      try {
        const { data } = await api.put(`/avito_listings/${listing.avito_id}/products`, {
          manager_tg_id: this.managerTgId,
          product_ids: productIds,
        });
        const newState = data.products.length ? 'mapped' : 'unmapped';
        this.moveListing(listing, newState, data);
        this.closePicker();
        this.showNotification(
          data.products.length
            ? `Привязано: ${data.products.map(p => p.name).join(', ')}`
            : 'Разметка снята',
          'success',
        );
      } catch (error) {
        this.handleApiError(error, 'сохранении разметки');
      } finally {
        this.busyId = null;
      }
    },

    async setIgnored(listing, isIgnored) {
      this.busyId = listing.avito_id;
      try {
        const { data } = await api.put(`/avito_listings/${listing.avito_id}/ignored`, {
          manager_tg_id: this.managerTgId,
          is_ignored: isIgnored,
        });
        this.moveListing(listing, isIgnored ? 'ignored' : 'unmapped', data);
        this.showNotification(isIgnored ? 'Отмечено «Не товар магазина»' : 'Объявление вернулось в разметку', 'success');
      } catch (error) {
        this.handleApiError(error, 'сохранении отметки');
      } finally {
        this.busyId = null;
      }
    },

    // Обновляет счётчики и убирает объявление из текущей вкладки, если оно в неё больше не попадает
    moveListing(listing, newState, data) {
      if (newState !== this.state) {
        this.listings = this.listings.filter(l => l.avito_id !== listing.avito_id);
        this.counts[this.state] -= 1;
        this.counts[newState] += 1;
      } else {
        Object.assign(listing, { products: data.products, is_ignored: data.is_ignored });
      }
    },

    openPicker(listing) {
      this.pickerFor = listing.avito_id;
      this.pickerQuery = '';
      this.pickerSelected = listing.products.map(p => p.id);
    },

    closePicker() {
      this.pickerFor = null;
      this.pickerQuery = '';
      this.pickerSelected = [];
    },

    openListing(listing) {
      if (!listing.url) return;
      if (window.Telegram?.WebApp?.openLink) {
        window.Telegram.WebApp.openLink(listing.url);
      } else {
        window.open(listing.url, '_blank');
      }
    },

    /* -------------------- UI -------------------- */

    // Ссылка на фото протухла или файла нет — оставляем пустую рамку вместо значка битой картинки
    hideBrokenImage(event) {
      event.target.style.visibility = 'hidden';
    },

    formatPrice(price) {
      return `${price.toLocaleString('ru-RU')} ₽`;
    },

    handleApiError(error, action) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (error.message || 'Неизвестная ошибка');
      console.error(`Ошибка при ${action}:`, error);
      this.showNotification(`Ошибка при ${action}: ${message}`, 'error');
    },

    showNotification(message, type = 'success') {
      clearTimeout(this.notificationTimer);
      this.notification = { show: true, message, type };
      this.notificationTimer = setTimeout(() => {
        this.notification.show = false;
      }, 4000);
    },
  },
};
</script>


<style scoped>
.bot-control {
    width: 100%;
    height: 10vw;
    border-radius: 10px;
    font-size: 18px;
    color: white;
    background-color: #005ADA;
    border: none;
    font-family: 'CentureGothic', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.bot-control:active {
    transform: scale(0.97);
}

.bot-control:disabled {
    background-color: #003D99;
    cursor: default;
}

.sync-hint {
    margin-top: 2vw;
    color: rgba(255, 255, 255, 0.5);
    font-family: 'CentureGothic', sans-serif;
    font-size: 12px;
    text-align: center;
}

/* Вкладки */
.tabs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2vw;
    margin: 3vw 0;
    padding: 1.5vw;
    background-color: rgba(255, 255, 255, 0.08);
    border-radius: 15px;
}

.tab {
    min-width: 0;
    padding: 2vw 1vw;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    font-family: 'CentureGothic', sans-serif;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s ease;
}

.tab.active {
    background-color: #005ADA;
    color: white;
}

.tab-count {
    margin-left: 1vw;
    padding: 0 1.5vw;
    border-radius: 10px;
    background-color: rgba(0, 0, 0, 0.4);
    font-size: 11px;
}

/* Карточки объявлений */
.listings {
    display: flex;
    flex-direction: column;
    gap: 3vw;
    padding-bottom: 20px;
}

.listing-card {
    padding: 3vw;
    border-radius: 10px;
    background-color: #1C1C1D;
    color: white;
    font-family: 'CentureGothic', sans-serif;
}

.listing-head {
    margin-bottom: 2vw;
}

.listing-title {
    color: white;
    font-size: 15px;
    text-decoration: none;
    word-break: break-word;
}

.listing-meta {
    display: flex;
    justify-content: space-between;
    gap: 2vw;
    margin-top: 1vw;
    color: #005ADA;
    font-size: 12px;
}

.listing-id {
    color: rgba(255, 255, 255, 0.4);
}

/* Привязанные товары */
.chips {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5vw;
    margin-bottom: 2vw;
}

.chip {
    display: inline-flex;
    align-items: center;
    gap: 1.5vw;
    padding: 1vw 2.5vw;
    border-radius: 10px;
    font-size: 12px;
}

.chip-mapped {
    background-color: rgba(11, 218, 81, 0.15);
    color: #0BDA51;
}

.chip-remove {
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
}

/* Подсказки */
.suggestions {
    display: flex;
    flex-direction: column;
    gap: 1.5vw;
    margin-bottom: 2vw;
}

.suggestion {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2vw;
    width: 100%;
    padding: 2.5vw 3vw;
    border: 2px solid #005ADA;
    border-radius: 10px;
    background-color: transparent;
    color: white;
    font-family: 'CentureGothic', sans-serif;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
}

.suggestion:active {
    background-color: rgba(0, 90, 218, 0.25);
}

.suggestion-name {
    flex: 1;
    min-width: 0;
    word-break: break-word;
}

/* Миниатюра товара в подсказках и поиске */
.product-thumb {
    flex-shrink: 0;
    width: 10vw;
    height: 10vw;
    border-radius: 8px;
    object-fit: cover;
    background-color: rgba(255, 255, 255, 0.08);
}

.product-thumb-empty {
    display: block;
}

.suggestion-reason {
    flex-shrink: 0;
    padding: 0.5vw 2vw;
    border-radius: 10px;
    background-color: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    font-size: 10px;
}

.reason-orders {
    color: #0BDA51;
}

.reason-similar_listing {
    color: #FFC107;
}

.no-suggestions {
    color: rgba(255, 255, 255, 0.4);
    font-size: 12px;
}

/* Кнопки действий */
.listing-actions,
.picker-actions {
    display: flex;
    gap: 2vw;
}

.btn {
    flex: 1;
    height: 9vw;
    border: none;
    border-radius: 10px;
    color: white;
    font-family: 'CentureGothic', sans-serif;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn:active {
    transform: scale(0.97);
}

.btn-primary {
    background-color: #005ADA;
}

.btn-secondary {
    background-color: rgba(255, 255, 255, 0.08);
}

.btn:disabled,
.suggestion:disabled {
    opacity: 0.5;
    cursor: default;
}

/* Выбор товара */
.search-input {
    width: 100%;
    padding: 12px 15px;
    background-color: transparent;
    color: white;
    border: 2px solid #005ADA;
    border-radius: 15px;
    font-family: 'CentureGothic', sans-serif;
    font-size: 14px;
    outline: none;
    transition: all 0.3s ease;
}

.search-input:focus {
    border-color: #0BDA51;
    box-shadow: 0 0 0 3px rgba(11, 218, 81, 0.2);
}

.search-input::placeholder {
    color: #666;
}

.picker-list {
    max-height: 65vw;
    margin: 2vw 0;
    overflow-y: auto;
}

.picker-item {
    display: flex;
    align-items: center;
    gap: 3vw;
    padding: 2.5vw 1vw;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 13px;
    cursor: pointer;
}

.picker-item input {
    accent-color: #005ADA;
}

.picker-hint {
    margin-bottom: 2vw;
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
}

/* Состояния */
.loading-state,
.empty-state {
    padding: 20px;
    color: #005ADA;
    font-family: 'CentureGothic', sans-serif;
    font-size: 16px;
    text-align: center;
}

.spinner {
    width: 32px;
    height: 32px;
    margin: 0 auto 10px;
    border: 3px solid rgba(0, 90, 218, 0.3);
    border-top-color: #005ADA;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.btn-loading {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Уведомление */
.notification {
    position: fixed;
    left: 4vw;
    right: 4vw;
    bottom: 5vw;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 3vw;
    padding: 3vw 4vw;
    border-radius: 15px;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(10px);
    color: white;
    font-family: 'CentureGothic', sans-serif;
    font-size: 13px;
}

.notification.success {
    border: 2px solid #0BDA51;
}

.notification.warning {
    border: 2px solid #FFC107;
}

.notification.error {
    border: 2px solid #F44336;
}

.notification-text {
    flex: 1;
}

.notification-close {
    border: none;
    background: none;
    color: rgba(255, 255, 255, 0.6);
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
}
</style>
